import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Tag, X, Save, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined color options
const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#64748b', label: 'Gray' },
];

// Label Card Component
const LabelCard = ({ label, onEdit, onDelete }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`label-card-${label.id}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className="w-4 h-4 rounded-full"
          style={{ backgroundColor: label.color }}
        />
        <span className="font-medium text-gray-800">{label.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(label)}
          className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
          data-testid={`edit-label-${label.id}`}
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onDelete(label)}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          data-testid={`delete-label-${label.id}`}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
    <p className="text-xs text-gray-400 mt-2">
      Created: {new Date(label.createdAt).toLocaleDateString()}
    </p>
  </div>
);

// Label Form Modal
const LabelModal = ({ isOpen, onClose, onSave, label, loading }) => {
  const [formData, setFormData] = useState({ name: '', color: '#6366f1' });

  useEffect(() => {
    if (label) {
      setFormData({ name: label.name, color: label.color });
    } else {
      setFormData({ name: '', color: '#6366f1' });
    }
  }, [label, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="label-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {label ? 'Edit Label' : 'Create New Label'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Label Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., VIP, Follow Up, Hot Lead"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              data-testid="label-name-input"
              required
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label Color</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                    formData.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                  data-testid={`color-${color.value}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-sm font-medium"
                style={{ backgroundColor: formData.color }}
              >
                <Tag size={14} />
                {formData.name || 'Label Name'}
              </span>
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim()}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="save-label-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {label ? 'Update Label' : 'Create Label'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, label, loading }) => {
  if (!isOpen || !label) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="delete-modal">
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 size={32} className="text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Delete Label</h2>
          <p className="text-gray-600">
            Are you sure you want to delete <strong>"{label.name}"</strong>? This will remove the label from all Properties and Leads.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-center gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="confirm-delete-btn"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Labels Page
const WhatsAppLabels = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);

  const fetchLabels = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setLabels(data.data || []);
    } catch (error) {
      console.error('Error fetching labels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const handleSaveLabel = async (formData) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (selectedLabel) {
        // Update existing label
        await fetch(`${API_URL}/api/whatsapp/labels/${selectedLabel.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        });
      } else {
        // Create new label
        await fetch(`${API_URL}/api/whatsapp/labels`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        });
      }

      await fetchLabels();
      setModalOpen(false);
      setSelectedLabel(null);
    } catch (error) {
      console.error('Error saving label:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLabel = async () => {
    if (!selectedLabel) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/whatsapp/labels/${selectedLabel.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchLabels();
      setDeleteModalOpen(false);
      setSelectedLabel(null);
    } catch (error) {
      console.error('Error deleting label:', error);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (label) => {
    setSelectedLabel(label);
    setModalOpen(true);
  };

  const openDeleteModal = (label) => {
    setSelectedLabel(label);
    setDeleteModalOpen(true);
  };

  const openCreateModal = () => {
    setSelectedLabel(null);
    setModalOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="labels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Labels</h1>
          <p className="text-gray-500 mt-1">Create and manage labels to organize your Properties and Leads</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-sm transition-colors"
          data-testid="create-label-btn"
        >
          <Plus size={18} />
          Create Label
        </button>
      </div>

      {/* Labels Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-teal-600" />
        </div>
      ) : labels.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Tag size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Labels Yet</h3>
          <p className="text-gray-500 mb-4">Create your first label to start organizing your Properties and Leads</p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
          >
            <Plus size={18} />
            Create Label
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {labels.map((label) => (
            <LabelCard
              key={label.id}
              label={label}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <LabelModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedLabel(null);
        }}
        onSave={handleSaveLabel}
        label={selectedLabel}
        loading={saving}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedLabel(null);
        }}
        onConfirm={handleDeleteLabel}
        label={selectedLabel}
        loading={saving}
      />
    </div>
  );
};

export default WhatsAppLabels;
